// /src/App.js
import React, { useMemo, useState, useEffect, createContext } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getAppTheme } from './theme';
import AppBarComponent from './components/AppBar';
import Navbar from './components/Navbar';
import DashboardPage from './pages/DashboardPage';
import IndividualSitesPage from './pages/IndividualSitesPage';
import IndividualProjectPage from './pages/IndividualProjectPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';  
import ProtectedRoute from './components/ProtectedRoute';
import UserAccessPage from './pages/UserAccessPage';
import { store } from './redux/store';
import { Provider } from 'react-redux';
import AdminLogsPage from './pages/AdminLogs';
import UserPermissionsPage from './pages/UserPermissions';
import FirebaseTestPage from './pages/FirebaseTestPage';
import LandingPage from './pages/LandingPage';
import ReportsAnalyticsPage from './pages/ReportsAnalyticsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Wrapper to conditionally include Navbar
const Layout = ({ children }) => {
  const location = useLocation();
  
  // Exclude Navbar and AppBar for these paths
  const excludePaths = ['/', '/login', '/signup', '/privacy-policy'];
  const showComponents = !excludePaths.includes(location.pathname);

  return (
    <>
      {showComponents && <AppBarComponent />}
      {showComponents && <Navbar />}
      {children}
    </>
  );
};

export const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: 'light' });

const App = () => {
  const [mode, setMode] = useState('light');

  // Detect system preference and initialize mode
  useEffect(() => {
    // Priority: localStorage > system preference > default 'light'
    const saved = typeof window !== 'undefined' ? localStorage.getItem('color-mode') : null;
    
    if (saved === 'dark' || saved === 'light') {
      setMode(saved);
    } else {
      // Check system preference if no saved preference
      if (typeof window !== 'undefined' && window.matchMedia) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setMode(prefersDark ? 'dark' : 'light');
      }
    }

    // Listen for system preference changes (only if no manual preference is set)
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e) => {
        // Only update if user hasn't manually set a preference
        const saved = localStorage.getItem('color-mode');
        if (!saved || (saved !== 'dark' && saved !== 'light')) {
          setMode(e.matches ? 'dark' : 'light');
        }
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      } 
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, []);

  const colorMode = useMemo(() => ({
    mode,
    toggleColorMode: () => {
      setMode(prev => {
        const next = prev === 'light' ? 'dark' : 'light';
        try { localStorage.setItem('color-mode', next); } catch {}
        return next;
      });
    }
  }), [mode]);

  const theme = useMemo(() => getAppTheme(mode), [mode]);
  return (
    <Provider store={store}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
        <Router>
          <Layout>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/individual-sites" element={<ProtectedRoute><IndividualSitesPage /></ProtectedRoute>} />
              {/* Hidden for now: Operations */}
              {/* <Route path="/operations" element={<ProtectedRoute><OperationsPage /></ProtectedRoute>} /> */}
              {/* Hidden for now: Maintenance */}
              {/* <Route path="/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} /> */}
              {/* Hidden for now: Energy Management */}
              {/* <Route path="/energy-management" element={<ProtectedRoute><EnergyManagementPage /></ProtectedRoute>} /> */}
              <Route path="/reports-analytics" element={<ProtectedRoute><ReportsAnalyticsPage /></ProtectedRoute>} />
              {/* Hidden for now: Alert Management */}
              {/* <Route path="/alert-management" element={<ProtectedRoute><AlertManagementPage /></ProtectedRoute>} /> */}
              {/* Hidden for now: Administration */}
              {/* <Route path="/administration" element={<ProtectedRoute><AdministrationPage /></ProtectedRoute>} /> */}
              {/* Hidden for now: Authentication page (not login/signup) */}
              {/* <Route path="/authentication" element={<AuthenticationPage />} /> */}
              <Route path="/individual-project" element={<ProtectedRoute><IndividualProjectPage /></ProtectedRoute>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/user-access" element={<ProtectedRoute><UserAccessPage /></ProtectedRoute>} />
              <Route path="/admin-logs" element={<ProtectedRoute><AdminLogsPage /></ProtectedRoute>} />
              <Route path="/user-permissions" element={<ProtectedRoute><UserPermissionsPage /></ProtectedRoute>} />
              <Route path="/firebase-test" element={<ProtectedRoute><FirebaseTestPage /></ProtectedRoute>} />
            </Routes>
          </Layout>
        </Router>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </Provider>
  );
};

export default App;
